import React from 'react';
import { Button, Spin, Typography } from 'antd';
import { ArrowRightOutlined, BankOutlined, LockOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export const isWorkspaceSelectable = (ws) =>
  ws?.selectable !== false && !ws?.login_disabled && ws?.company_approved !== false;

export const workspaceDisabledReason = (ws) => {
  if (ws?.login_disabled) return 'ورود به این پنل غیرفعال است';
  if (ws?.company_approved === false) return 'شرکت هنوز تأیید نشده است';
  if (ws?.selectable === false) return 'این پنل قابل انتخاب نیست';
  return null;
};

const WorkspacePicker = ({
  workspaces = [],
  loadingKey = null,
  isDarkMode,
  onSelect,
  onBack,
}) => {
  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onBack}
        disabled={!!loadingKey}
        className={`mb-6 flex items-center gap-2 text-sm font-medium transition-colors ${
          isDarkMode ? 'text-white/70 hover:text-brand-primary' : 'text-gray-600 hover:text-gray-900'
        } disabled:opacity-50`}
        style={{ fontFamily: 'CustomFont' }}
      >
        <ArrowRightOutlined />
        بازگشت به ورود
      </button>

      <div className="text-center mb-8">
        <Title
          level={2}
          className={`!mb-2 !font-bold ${isDarkMode ? '!text-white' : '!text-gray-800'}`}
          style={{ fontFamily: 'CustomFont' }}
        >
          انتخاب پنل کاری
        </Title>
        <Text
          className={`text-base ${isDarkMode ? 'text-white/65' : 'text-gray-600'}`}
          style={{ fontFamily: 'CustomFont' }}
        >
          حساب شما به چند شرکت متصل است. پنل مورد نظر را انتخاب کنید.
        </Text>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {workspaces.map((ws) => {
          const selectable = isWorkspaceSelectable(ws);
          const disabledReason = workspaceDisabledReason(ws);
          const key = `${ws.user_id}-${ws.company_id}`;
          const isLoading = loadingKey === key;

          return (
            <button
              key={key}
              type="button"
              disabled={!selectable || !!loadingKey}
              onClick={() => selectable && onSelect(ws)}
              className={`
                group relative flex w-full flex-col items-center rounded-2xl border-2 p-6 text-center
                transition-all duration-200
                ${selectable && !loadingKey
                  ? isDarkMode
                    ? 'border-[rgba(13,136,126,0.22)] bg-brand-dark hover:border-brand-primary hover:bg-[rgba(13,136,126,0.08)] hover:shadow-lg hover:shadow-[rgba(13,136,126,0.12)]'
                    : 'border-gray-200 bg-white hover:border-brand-primary hover:shadow-xl hover:shadow-[rgba(13,136,126,0.12)]'
                  : isDarkMode
                    ? 'cursor-not-allowed border-[rgba(13,136,126,0.12)] bg-brand-dark opacity-50'
                    : 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-70'
                }
              `}
            >
              {isLoading ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-black/20 backdrop-blur-[2px]">
                  <Spin />
                </div>
              ) : null}

              <div
                className={`mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl shadow-md ring-2 ${
                  isDarkMode
                    ? 'bg-[rgba(13,136,126,0.12)] ring-[rgba(13,136,126,0.22)]'
                    : 'bg-gray-100 ring-white'
                }`}
              >
                {ws.logo_url ? (
                  <img
                    src={ws.logo_url}
                    alt=""
                    className="max-h-[4.5rem] max-w-[4.5rem] object-contain p-1.5"
                  />
                ) : (
                  <BankOutlined className={`text-3xl ${isDarkMode ? 'text-brand-primary' : 'text-gray-500'}`} />
                )}
              </div>

              <h3
                className={`mb-1 text-lg font-bold leading-snug ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                style={{ fontFamily: 'CustomFont' }}
              >
                {ws.company_name || '—'}
              </h3>
              <p
                className={`mb-3 text-sm ${isDarkMode ? 'text-white/65' : 'text-gray-500'}`}
                style={{ fontFamily: 'CustomFont' }}
              >
                {ws.user_name || '—'}
              </p>

              {selectable ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-brand-primary px-4 py-1.5 text-xs font-semibold text-white opacity-90 transition-opacity group-hover:opacity-100"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ورود به پنل
                  <ArrowRightOutlined className="text-[10px] rotate-180" />
                </span>
              ) : (
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${
                    isDarkMode
                      ? 'bg-[rgba(13,136,126,0.12)] text-white/50'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  <LockOutlined />
                  {disabledReason}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {workspaces.length === 0 ? (
        <div className="mt-6 text-center">
          <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
            پنلی برای نمایش یافت نشد.
          </Text>
          <div className="mt-4">
            <Button onClick={onBack} style={{ fontFamily: 'CustomFont' }}>
              بازگشت
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WorkspacePicker;
